import { Tearsheet } from '@carbon/ibm-products';
import { Loading } from '@carbon/react';
import { useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useUpdateOfferMutation } from 'api/offerEndpoints';
import { OfferDto } from 'api/dtos/offer';
import OfferForm, { OfferFormHandle, OfferFormValues } from '../OfferForm/OfferForm';

import styles from './EditOfferView.module.scss';

type Props = {
    offer: OfferDto | null;
    open: boolean;
    onClose: () => void;
}

const formatDateTimeLocal = (iso?: string) => {
    if (!iso) return undefined;

    const date = new Date(iso);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
};

const EditOfferView = ({
    offer,
    open,
    onClose,
}: Props) => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const [updateOffer, { isLoading }] = useUpdateOfferMutation();
    const formRef = useRef<OfferFormHandle>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const initialValues = useMemo<Partial<OfferFormValues> | undefined>(() => {
        if (!offer) return undefined;

        return {
            name: offer.name,
            description: offer.description,
            companyName: offer.companyName,
            originalPrice: offer.originalPrice,
            discountedPrice: offer.discountedPrice,
            offerUrl: offer.offerUrl,
            imageUrl: offer.imageUrl,
            startAt: formatDateTimeLocal(offer.startAt),
            endAt: formatDateTimeLocal(offer.endAt),
            applyToChildren: offer.applyToChildren ?? false,
        };
    }, [offer]);

    const handleSubmit = async (data: OfferFormValues) => {
        if (!currentEntityId || !offer) return;
        setErrorMessage('');

        try {
            await updateOffer({
                entityId: currentEntityId,
                id: offer.id,
                data,
            }).unwrap();
            onClose();
        } catch {
            setErrorMessage(intl.formatMessage(messages.submitError));
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={offer?.name ?? intl.formatMessage(messages.title)}
                description={intl.formatMessage(messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={onClose}
                selectorPrimaryFocus="#offer-name"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: onClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled || !offer,
                        loading: isLoading,
                        onClick: () => formRef.current?.submit(),
                    },
                ]}
            >
                {open && offer && (
                    <div className={styles.form}>
                        {errorMessage && (
                            <div className={styles.error}>{errorMessage}</div>
                        )}
                        <OfferForm
                            key={offer.id}
                            ref={formRef}
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            hideActions
                            onDisabledChange={setIsSubmitDisabled}
                        />
                    </div>
                )}
            </Tearsheet>
        </>
    );
};

const messages = defineMessages({
    title: {
        id: 'offers.edit.title',
        defaultMessage: 'Edytuj ofertę',
    },
    description: {
        id: 'offers.edit.description',
        defaultMessage: 'Zmień dane oferty i zapisz aktualizację.',
    },
    submitError: {
        id: 'offers.edit.submitError',
        defaultMessage: 'Nie udało się zaktualizować oferty. Sprawdź dane i spróbuj ponownie.',
    },
});

export default EditOfferView;
